/**
 * This explanation is to explain how tab info in dialog is displayed.
 *
 * tcdd-train-tab.ts class is used to fetch, add, update, or remove
 * the tab info panel (just below the SVG files) in the TCDD train HTML structure.
 * The core workflow is as follows:
 *
 * 1. Retrieving MainTab Models
 *    - `tcdd-train-tab.ts` returns an array of `MainTab[]`,
 *      each representing a primary system (Main Tab).
 *    - Each MainTab contains:
 *        • `id`       – Unique identifier
 *        • `title`    – Tab title (e.g., “Diagnostic System”)
 *        • `subTabs`  – Array of subtabs under that main system
 *                       (e.g., “Traction”, “Brake”, “Power & Voltage”, "Door" etc.)
 *
 * 2. SubTab Models and Info Groups
 *    - Each subtab (`SubTab`) includes:
 *        • `id`
 *        • `title`
 *        • `mainInfoGroups` – Info groups shown in the train-level view
 *        • `subInfoGroups`  – Info groups shown in the wagon-level view
 *    - Which group to use is determined by the `currentView` variable:
 *        – `currentView === TRAIN_OVERVIEW` → Train-level display (`mainInfoGroups`)
 *        – `currentView === WAGON_DETAIL` → Wagon-level display (`subInfoGroups`)
 *        – Other values → Customized variations of the above two modes
 *
 * 3. The updateGroupColumns Method for View Updates
 *    - When a user selects a main system and a sub-system:
 *        1. The corresponding `MainTab` and `SubTab` models are retrieved.
 *        2. Based on `view`, the appropriate `InfoGroup[]`
 *           (either mainInfoGroups or subInfoGroups) is chosen.
 *        3. Any necessary filtering or transformations are applied.
 *        4. The resulting `InfoGroup` array is split into:
 *            • `leftGroups`  → Status information
 *            • `rightGroups` → Error information
 *        5. These groups are displayed in the dialog in the order:
 *           statuses first, then errors—order is crucial.
 *
 * 4. InfoGroup Structure
 *    - Each `InfoGroup` contains:
 *        • `id`
 *        • `title`
 *        • `MatrixItem[]` – Indicates whether the info should be
 *          rendered in a matrix format or as a list.
 *        • `visibility`
 *        • `InfoItem[]`   – Defines the properties of each
 *          displayed info entry (one row/item per InfoItem).
 *
 * 5. Rendering Templates for InfoGroups
 *    - InfoGroups can be rendered in three different templates:
 *         • `coupling`      – Used in train-level displays
 *         • `matrixSub`     – Used in wagon-level displays when
 *                             a matrix view is present
 *         • `default`       – A fallback template available in both contexts
 *     - The actual template selection is performed by the
 *       `getGroupTemplate` method.
 *
 * In summary:
 * • All main and sub system tabs are represented
 *   by `MainTab` and `SubTab` models.
 * • The `view` parameter selects between train-level
 *   and wagon-level displays.
 * • The `updateGroupColumns` method processes the chosen
 *   model array and assigns `leftGroups`/`rightGroups`,
 *   ensuring correct ordering of status and error info
 *   in the dialog.
 * • Template rendering for each InfoGroup is handled
 *   by `getGroupTemplate`, choosing between `coupling`,
 *   `matrixSub`, and `default` views.
 */

import {AfterViewInit, ChangeDetectorRef, Component, HostBinding, Injector, Input, OnDestroy, OnInit} from '@angular/core';
import {BaseDialogTemplateComponent} from '../base-dialog-template.component';
import {SVGManagerUtil} from '../../../../../shared/utils/svg-manager-util';
import {SvgTcddTrainRules} from '../../../../../shared/svg-rules/tcdd/svg-tcdd-train-rules';
import {SvgTcddTrainClickHandlers} from '../../../../../shared/svg-click/svg-tcdd-train-click-handlers';
import {TcddTrainTab} from '../../../../../shared/components/tab/tcdd-train-tab';
import {TcddTrainSystemLabels} from '../../../../../shared/components/label/tcdd-train-system-labels';
import {TcddWagonTitle} from '../../../../../shared/enums/index-map/tcdd-wagon-title.enum';
import {TcddTrainWagonsIndexManagerUtil} from '../../../../../shared/utils/tcdd-train-wagons-index-manager-util';
import {TcddTrainDataManagerService} from '../../../../../core/services/tcdd-train-data-manager.service';
import {TcddTrainErrorDetectionService} from '../../../../../core/services/tcdd-train-error-detection.service';
import {TcddTrainTextColorRules} from '../../../../../shared/color-rules/tcdd-train-text-color-rules';
import {Color} from '../../../../../shared/enums/color';
import {TranslateService} from '@ngx-translate/core';
import {MainTab} from '../../../../../shared/models/tcdd-train/main-tab.model';
import {SubTab} from '../../../../../shared/models/tcdd-train/sub-tab.model';
import {InfoGroup} from '../../../../../shared/models/tcdd-train/info-group.model';
import {InfoItem} from '../../../../../shared/models/tcdd-train/info-item.model';
import {TcddTrainTabUtil, ValueIndex} from '../../../../../shared/utils/tcdd-train-tab-util';
import {DomSanitizer} from '@angular/platform-browser';
import {PathInfo} from '../../../../../shared/models/tcdd-train/path-info.model';
import {TcddMainSystem} from '../../../../../shared/enums/tcdd-main-system.enum';
import {TcddSubSystem} from '../../../../../shared/enums/tcdd-sub-system.enum';
import {TcddSubSystemType} from '../../../../../shared/enums/tcdd-sub-system-type.enum';
import {TcddDialogView} from '../../../../../shared/enums/tcdd-dialog-view.enum';
import {
  badgeMap, brakeMode, couplingHeaders, dmiStatus, hvacMode, iconMap,
  interiorLightingModes, pantographMap, svgMap, svgPaths, textMap, vacuumMap
} from '../../../../../shared/models/tcdd-train/mappings';
import {MainSystemKeys} from '../../../../../shared/constants/tcdd-system-keys/main-system-keys';
import {AirConditionSystemKeys} from '../../../../../shared/constants/tcdd-system-keys/air-condition-system-keys';
import {OtherSystemKeys} from '../../../../../shared/constants/tcdd-system-keys/other-system-keys';
import {environment} from '../../../../../../environments/environment';
import {TCDD_UNIT} from '../../../../../shared/constants/tcdd-unit-constant';
import {PmService} from '../../../../../core/services/pm.service';
import {StackService} from '../../../../../core/services/stack.service';
import {ConnectionUtil, ConnectionStatus, GsmConnectionType,
  SignalQualityMap, SignalThresholds} from '../../../../../shared/utils/connection-util';

enum State {
  NoError = 0,  // No error
  Error = 1,    // Error exists
}

enum TrainType {
  ACTIVE = 'active',
  PASSIVE = 'passive',
  NONE = ''
}

enum VisualElementType {
  BADGE = 'badge',
  SVG = 'svg',
  TEXT = 'text',
  ICON = 'icon',
  NONE = 'none'
}

interface PMSource {
  type: string;
  value: string;
  observedAt: string;
}

interface PMItem {
  sourceSignal: PMSource[];
}

interface PMOutput {
  anomalySignal: string;
  setNo: string;
  vehicleName: string;
  subsystem: string;
  portName: string;
  anomalyCount: number;
  firstAnomalyDatetime: string;
  lastAnomalyDatetime: string;
}

interface StackItem {
  pathInfo: PathInfo;
  selectedTitle: string;
}

type GroupTemplate = 'coupling' | 'matrixSub' | 'default' | 'none';

// Define map and thresholds (could also be loaded from config)
const signalQualityMap: SignalQualityMap = {
  0: 'criticalSignal',
  1: 'lowSignal',
  2: 'normalSignal',
  3: 'goodSignal',
  4: 'noSignal',
};

const thresholds: SignalThresholds = {
  [GsmConnectionType.TWO_G]: [
    { operator: '<=', value: -100, mapIndex: 0 },
    { operator: '<=', value: -90, mapIndex: 1 },
    { operator: '<=', value: -80, mapIndex: 2 },
    { operator: '>',  value: -80, mapIndex: 3 }
  ],
  [GsmConnectionType.THREE_G]: [
    { operator: '<=', value: -110, mapIndex: 0 },
    { operator: '<=', value: -100, mapIndex: 1 },
    { operator: '<=', value: -85, mapIndex: 2 },
    { operator: '>',  value: -85, mapIndex: 3 }
  ],
  [GsmConnectionType.FOUR_G]: [
    { operator: '<=', value: -105, mapIndex: 0 },
    { operator: '<=', value: -95, mapIndex: 1 },
    { operator: '<=', value: -85, mapIndex: 2 },
    { operator: '>',  value: -85, mapIndex: 3 }
  ]
};

@Component({
  selector: 'tcdd-train-dialog-template',
  templateUrl: './tcdd-train-dialog-template.component.html',
  styleUrls: ['./tcdd-train-dialog-template.component.scss']
})
export class TcddTrainDialogTemplateComponent extends BaseDialogTemplateComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input() cBrokerEndpoint: string;

  private static readonly COORD_DECIMALS = 3; // Constant for the number of digits after the decimal point of the location
  private static readonly DEFAULT_PLACEHOLDER = '-'; // Constant for default placeholder
  protected readonly TcddDialogView = TcddDialogView; // for html
  protected readonly TcddMainSystem = TcddMainSystem; // for html
  protected readonly TcddSubSystem = TcddSubSystem; // for html

  private svgManager!: SVGManagerUtil; // The class used to manage the SVG files of the active train.
  private svgManagerPassive?: SVGManagerUtil; // The class used to manage the SVG files of the passive train.
  private dataManager: TcddTrainDataManagerService;
  private pmService: PmService;
  private errorDetection: TcddTrainErrorDetectionService;
  private textColorRules: TcddTrainTextColorRules;

  // Indices
  private lightOnIndex!: ValueIndex;
  private halfLightIndex!: ValueIndex;
  private emergencyLightIndex!: ValueIndex;
  private tempOutsideIndex!: ValueIndex;

  // active train parameters
  trainEntity: any = null;
  trainNo: any = '';
  activeDeviceStatus: any = null;
  activeDeviceErrors: any = null;
  numberOfWagons: number = 0;
  activeSignalStatus!: ConnectionStatus;

  // passive train parameters
  isCoupling: boolean = false;
  secondTrainEntity: any = null;
  secondTrainNo: any = '';
  passiveDeviceStatus: any = null;
  passiveDeviceErrors: any = null;
  secondNumberOfWagons: number = 0;
  passiveSignalStatus?: ConnectionStatus;

  // Common parameters
  position: any = null;
  location: any = null;
  speed: string = '';

  wagonsData: { [key: string]: any } = {}; // Selected train wagons data (same as activeWagonsData or passiveWagonsData)
  trainWagonTitles: any[] = []; // Selected train wagon titles (same as activeTrainWagonTitles or passiveTrainWagonTitles)
  activeTrainData: { [key: string]: any } = {};
  activeWagonsData: { [key: string]: any } = {};
  activeTrainWagons: any[] = [];
  activeTrainWagonTitles: any[] = [];
  passiveTrainData: { [key: string]: any } = {};
  passiveWagonsData: { [key: string]: any } = {};
  passiveTrainWagons: any[] = [];
  passiveTrainWagonTitles: any[] = [];
  pmOutputMap: PMOutput[] = []; // for prognostic system

  mainTabsError: number[] = [State.NoError, State.NoError];
  activeMainTab: string = ''; // e.g., diagnosticSystem, prognosticSystem
  activeSubTab: string = ''; // e.g., tractionSystem, brakeSystem, doorSystem
  activeSubTabSVGPath: string | null = null; // e.g., assets/images/train/train-traction, assets/images/train/train-brake
  currentSVGPath: string | null = null;
  previousSVGPath: string | null = null;
  defaultSVGPath: string = '';
  pathInfo: PathInfo = null;
  currentView: TcddDialogView; // The visibility of the information under the tab can be controlled. When the entire train is visibility (TRAIN_OVERVIEW), clicking on any wagon changes currentView to WAGON_DETAIL.
  selectedWagonTitle: string | null; // It is used to track which wagon is clicked (e.g., SKA1, OA3).
  selectedUnitTitle: string | null;
  selectedTrain: TrainType; // It is used to track which train is selected during the click event. Accordingly, the information under the tabs is displayed (e.g., activeTrain, passiveTrain).

  mainTabs: MainTab[] = TcddTrainTab.getTabInfo(); // The variable that holds all the information displayed in the tabs in HTML.
  systemLabels = new TcddTrainSystemLabels();
  trainStatus: string = '';
  outsideAirTemperature: string = '-';

  isActiveClickEnabled: boolean = true;
  isActiveModificationEnabled: boolean = true;
  isPassiveClickEnabled: boolean = true;
  isPassiveModificationEnabled: boolean = true;
  isOldEnough: boolean = false;
  isDataLoaded: boolean = false;
  interiorLightingInfoVisibility: boolean = false;
  hasLeftColumnStatues: boolean; // If there is a status in leftGroups, this flag will be true.
  hasRightColumnErrors: boolean; // If there is an error in rightGroups, this flag will be true.
  leftGroups: InfoGroup[] = [];
  rightGroups: InfoGroup[] = [];
  couplingHeaders = couplingHeaders; // Headers for train coupling.
  interiorLightingModes = interiorLightingModes; // Mapping for interior lighting modes.

  progress: number = 0;

  private containerActive: HTMLElement;
  private containerPassive: HTMLElement;
  private modelViewerContainer: HTMLElement;
  private backIcon: HTMLElement;
  private prevIcon: HTMLElement;
  private nextIcon: HTMLElement;
  DEPLOYMENT_TCDD = 'tcdd';

  constructor(protected injector: Injector,
              private cdr: ChangeDetectorRef,
              private translate: TranslateService,
              private sanitizer: DomSanitizer,
              private stackService: StackService<StackItem>) {
    super(injector);
    this.dataManager = new TcddTrainDataManagerService();
    this.errorDetection = new TcddTrainErrorDetectionService();
    this.textColorRules = new TcddTrainTextColorRules();
    this.pmService = injector.get(PmService);
  }

  ngOnInit(): void {
    super.ngOnInit();
    if (environment.deployment === this.DEPLOYMENT_TCDD) {
      const diagnosticMainTab = this.mainTabs.find(mt => mt.id === TcddMainSystem.DIAGNOSTIC_SYSTEM);
      if (diagnosticMainTab) {
        this.removeSubTabById(diagnosticMainTab, TcddSubSystem.SIGNALIZATION_SYSTEM);
      }
      this.changeTopValue('0');
    }
    this.updateSubTabWidth(TcddMainSystem.DIAGNOSTIC_SYSTEM);

    this.trainStatus = 'avaliable';
    this.activeMainTab = TcddMainSystem.DIAGNOSTIC_SYSTEM;
    this.activeSubTab = TcddSubSystem.TRACTION_SYSTEM;
    this.defaultSVGPath = svgPaths[this.activeSubTab];
    this.currentView = TcddDialogView.TRAIN_OVERVIEW;
    this.selectedWagonTitle = null;
    this.selectedUnitTitle = null;
    this.selectedTrain = TrainType.NONE;
    this.hasLeftColumnStatues = false;
    this.hasRightColumnErrors = false;

    this.stackService.stackObservable.subscribe(stack => {
      // console.log('Stack: ', stack);
      const stackItem: StackItem = this.stackService.current() ?? null;

      this.pathInfo = stackItem?.pathInfo ?? null;
      this.currentView = this.pathInfo?.view ?? TcddDialogView.TRAIN_OVERVIEW;
      this.previousSVGPath = this.pathInfo?.path ?? this.activeSubTabSVGPath;
      this.selectedUnitTitle = this.isUnit() ? stackItem?.selectedTitle ?? null : null;
    });
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  ngAfterViewInit() {
  }

  private getPM() {
    const now = new Date();
    const endTime = this.formatISO(now);

    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const startTime = this.formatISO(yesterday);
    const limit = 100000;
    this.pmService.getPMValues(startTime, endTime, limit, this.cBrokerEndpoint)
      .subscribe({
        next: (data: PMItem[]) => {
          data.forEach(item => {
            item.sourceSignal.forEach(s => {
              const val = s.value;
              const time = s.observedAt;
              const parts = val.split('_');

              const subSystem = parts[0] || '';
              const wagonType = parts[1] || '';
              const port = parts[2] || '';

              const existingItem = this.pmOutputMap.find(itemPM => itemPM.anomalySignal === val);

              if (!existingItem) {
                this.pmOutputMap.push({
                  anomalySignal: val,
                  setNo: this.trainNo,
                  vehicleName: wagonType,
                  subsystem: subSystem,
                  portName: port,
                  anomalyCount: 1,
                  firstAnomalyDatetime: time,
                  lastAnomalyDatetime: time
                });
              } else {
                existingItem.anomalyCount += 1;
                if (time < existingItem.firstAnomalyDatetime) existingItem.firstAnomalyDatetime = time;
                if (time > existingItem.lastAnomalyDatetime) existingItem.lastAnomalyDatetime = time;
              }
            });
          });
        },
        error: (err) => {
          console.error('Error fetching PM values:', err);
        }
      });
  }

  private formatISO(date: Date): string {
    return date.toISOString();
  }

  private removeSubTabById(mainTab: MainTab, subTabId: TcddSubSystem): void {
    mainTab.subTabs = mainTab.subTabs.filter(st => st.id !== subTabId);
  }

  @HostBinding('style.--tab-width') tabWidth: string;
  updateSubTabWidth(mainSystemId: TcddMainSystem): void {
    const targetTab = this.mainTabs.find(mt => mt.id === mainSystemId);

    const tabCount = targetTab?.subTabs.length ?? 1;

    this.tabWidth = `${79 / tabCount}vw`;
  }

  @HostBinding('style.--nb-main-tab-top') topValue: string = '35px';
  changeTopValue(value: string) {
    this.topValue = value;
  }

  get isFullHeight(): boolean {
    const noGroups = (this.leftGroups?.length ?? 0) === 0 && (this.rightGroups?.length ?? 0) === 0;
    const isCommunicationTab = this.activeSubTab === TcddSubSystem.COMMUNICATION_SYSTEM &&
      [TcddDialogView.TRAIN_OVERVIEW, TcddDialogView.WAGON_DETAIL, TcddDialogView.UNIT_VISUAL, TcddDialogView.UNIT_3D].includes(this.currentView);

    return [TcddDialogView.UNIT_VISUAL, TcddDialogView.UNIT_3D].includes(this.currentView) || (isCommunicationTab && noGroups);
  }

  get showImageTitleContainer(): boolean {
    return [
      TcddDialogView.UNIT_DETAIL,
      TcddDialogView.UNIT_VISUAL,
      TcddDialogView.UNIT_3D,
      TcddDialogView.UNIT_3D_DETAIL
    ].includes(this.currentView);
  }

  public onProgress(event: any): void {
    this.progress = event.detail.totalProgress;
  }

  public onModelLoad(): void {
    this.progress = 1;
  }

  public onModelError(event: any): void {
    this.progress = 0;
  }

  get is2DVisible(): boolean {
    return Boolean(
      this.pathInfo?.path &&
      [TcddDialogView.UNIT_3D_DETAIL, TcddDialogView.UNIT_3D].includes(this.currentView)
    );
  }

  get is3DVisible(): boolean {
    return Boolean(
      environment.deployment !== this.DEPLOYMENT_TCDD &&
      this.pathInfo?.path_3D &&
      [TcddDialogView.UNIT_DETAIL, TcddDialogView.UNIT_VISUAL].includes(this.currentView)
    );
  }

  public open2DViewer(): void {
    this.progress = 0;
    if (!this.pathInfo?.path) {
      console.warn('2D path not available');
      return;
    }

    if (this.currentView === TcddDialogView.UNIT_3D) this.currentView = TcddDialogView.UNIT_VISUAL;
    if (this.currentView === TcddDialogView.UNIT_3D_DETAIL) this.currentView = TcddDialogView.UNIT_DETAIL;

    this.currentSVGPath = this.previousSVGPath;
    this.updateGroupColumns();
    this.loadAndModifySVG(this.pathInfo.path);
  }

  public open3DViewer(): void {
    if (!this.pathInfo?.path_3D) {
      console.warn('3D path not available');
      return;
    }

    if (this.currentView === TcddDialogView.UNIT_VISUAL) this.currentView = TcddDialogView.UNIT_3D;
    if (this.currentView === TcddDialogView.UNIT_DETAIL) this.currentView = TcddDialogView.UNIT_3D_DETAIL;

    this.currentSVGPath = this.previousSVGPath;
    this.updateGroupColumns();
    this.loadAndModifySVG(this.pathInfo.path_3D);
  }

  toggleInteriorLightingStatuses(): void {
    this.interiorLightingInfoVisibility = !this.interiorLightingInfoVisibility;
  }

  public getPrevIconClass(): string {
    if (this.selectedWagonTitle !== TcddWagonTitle.SKA1) return '';
    switch (this.activeSubTab) {
      case TcddSubSystem.OTHER_SYSTEM:
        return 'prev-icon-for-other-system';
      case TcddSubSystem.DOOR_SYSTEM:
        return 'prev-icon-for-door-system';
      case TcddSubSystem.COMMUNICATION_SYSTEM:
        return 'prev-icon-for-communication-system';
      default:
        return '';
    }
  }

  public getNextIconClass(): string {
    if (this.selectedWagonTitle !== TcddWagonTitle.SKA2) return '';
    switch (this.activeSubTab) {
      case TcddSubSystem.OTHER_SYSTEM:
        return 'next-icon-for-other-system';
      case TcddSubSystem.DOOR_SYSTEM:
        return 'next-icon-for-door-system';
      case TcddSubSystem.COMMUNICATION_SYSTEM:
        return 'next-icon-for-communication-system';
      default:
        return '';
    }
  }

  public getLevelClass(p: PMOutput): string {
    if (p.anomalyCount < 5) return 'green';
    if (p.anomalyCount > 7) return 'orange';
    return 'yellow';
  }

  /**
   * Returns the appropriate template for the given group object.
   *
   * @param group - The group object expected to have a 'matrix' property.
   * @returns One of the template names: 'coupling', 'matrixSub', or 'default'.
   */
  public getGroupTemplate(group: InfoGroup): GroupTemplate {
    // For TRAIN_OVERVIEW: 'coupling' if coupling is enabled, default if not
    if (this.currentView === TcddDialogView.TRAIN_OVERVIEW) {
      return this.isCoupling ? 'coupling' : 'default';
    }

    // For WAGON_DETAIL or UNIT_DETAIL or UNIT_3D_DETAIL: 'matrixSub' if a matrix array is full
    if ((this.currentView === TcddDialogView.WAGON_DETAIL ||
        this.currentView === TcddDialogView.UNIT_DETAIL ||
        this.currentView === TcddDialogView.UNIT_3D_DETAIL) &&
      group?.matrix?.length > 0) {
      return 'matrixSub';
    }

    if (this.currentView === TcddDialogView.UNIT_VISUAL || this.currentView === TcddDialogView.UNIT_3D) {
      return 'none';
    }

    // Other situations
    return 'default';
  }

  /**
   * Determines whether a row should be visible based on the current column index, the provided values, and an error trigger condition.
   *
   * For the left column (index 0):
   *   - If left column statuses are available (hasLeftColumnStatues is true), the row is visible
   *     when at least one value is not the default placeholder.
   *   - Otherwise, the row is visible only when a value exactly matches the error trigger.
   *
   * For the right column (index 1):
   *   - If an error exists for the right column (hasRightColumnErrors is true), the row is visible
   *     only when a value exactly matches the error trigger.
   *   - Otherwise, the row is visible when at least one value is not the default placeholder.
   *
   * @param columnIndex - The index of the current column.
   * @param values - An object containing the primaryValue and secondaryValue.
   * @param errorTrigger - The value that indicates an error condition.
   * @returns True if the row should be visible; otherwise, false.
   */
  public isSingleRowVisible(columnIndex: number, values: { primaryValue: string; secondaryValue: string }, errorTrigger: string): boolean {
    const placeholderFirst = this.isPlaceholderPriority(columnIndex);

    return placeholderFirst
      ? this.hasNonPlaceholderValue(values)
      : this.hasTriggerValue(values, errorTrigger);
  }

  /**
   * Determines the visibility of a matrix row based on sub-info values.
   *
   * @param group - The data group containing the matrix and the associated infoList.
   * @param columnIndex - The index of the current column.
   * @param groupIndex - The index of the current row within the column.
   * @param sectionIndex - The index of the current section.
   * @returns True if at least one sub-info value satisfies the visibility condition; otherwise, false.
   */
  public isMatrixRowVisible(group: InfoGroup, columnIndex: number, groupIndex: number, sectionIndex: number): boolean {
    const sectionSize: number = group?.matrix?.length ?? 0;
    const startIndex: number = sectionIndex * sectionSize;
    const endIndex: number = startIndex + sectionSize;

    const sectionInfos: InfoItem[] = group.infoList.slice(startIndex, endIndex);

    return sectionInfos.some((info: InfoItem, localIndex: number) => {
      const infoIndex = startIndex + localIndex;
      const values = this.getDynamicValues(this.calculateGroupIndex(columnIndex, groupIndex), infoIndex);

      const isVisible = this.isSingleRowVisible(columnIndex, values, info.visualElementTrigger);

      return isVisible && (info.visibility ?? true);
    });
  }

  /**
   * Returns true if either primary or secondary value is not the placeholder.
   */
  private hasNonPlaceholderValue(values: { primaryValue: string; secondaryValue: string }): boolean {
    const {primaryValue, secondaryValue} = values;
    if (primaryValue == null && secondaryValue == null) return true; // To show that data is unknown
    return (
      (!!primaryValue && primaryValue !== TcddTrainDialogTemplateComponent.DEFAULT_PLACEHOLDER) ||
      (!!secondaryValue && secondaryValue !== TcddTrainDialogTemplateComponent.DEFAULT_PLACEHOLDER)
    );
  }

  /**
   * Returns true if either primary or secondary value matches the error trigger.
   */
  private hasTriggerValue(values: { primaryValue: string; secondaryValue: string }, errorTrigger: string): boolean {
    const {primaryValue, secondaryValue} = values;
    if (primaryValue == null && secondaryValue == null) return true; // To show that data is unknown
    return (
      (!!primaryValue && primaryValue === errorTrigger) ||
      (!!secondaryValue && secondaryValue === errorTrigger)
    );
  }

  /**
   * Determines if placeholder check has priority based on column index.
   * - Left column (index 0): if left statuses exist, check placeholder first.
   * - Right column (index 1): if right errors exist, check trigger first (so placeholder priority is inverted).
   */
  private isPlaceholderPriority(columnIndex: number): boolean {
    if (columnIndex === 0) {
      return this.hasLeftColumnStatues;
    } else if (columnIndex === 1) {
      // For right column: placeholder priority when there are no right column errors
      return !this.hasRightColumnErrors;
    }
    return true;
  }

  /**
   * Calculates the indices for each section based on the matrix size.
   *
   * @param group - The object containing 'infoList' and 'matrix' properties.
   * @returns Array of indices representing each section.
   */
  public getSectionIndices(group: InfoGroup): number[] {
    const totalSections: number = this.calculateSectionCount(group);
    return Array.from({length: totalSections}, (_, index) => index);
  }

  /**
   * Determines the number of sections based on the matrix.
   *
   * @param group - The object containing 'infoItems' and 'matrix' properties.
   * @returns Number of sections required.
   */
  private calculateSectionCount(group: InfoGroup): number {
    // Ensure that 'infoList' exists and that 'matrix' is a non-empty array.
    if (group?.infoList && group?.matrix?.length > 0) {
      return Math.ceil(group.infoList.length / group.matrix.length);
    }
    return 0;
  }

  /**
   * Calculates the group index based on the column index and the provided group index for that column.
   *
   * @param columnIndex - The index of the column (0 for left, 1 for right).
   * @param groupIndexForColumn - The group index specific to the given column.
   * @returns The calculated group index, or null if the colIndex is unsupported.
   */
  public calculateGroupIndex(columnIndex: number, groupIndexForColumn: number): number {
    switch (columnIndex) {
      case 0:
        // For the first column, return the index directly.
        return groupIndexForColumn;
      case 1:
        // For the second column, return the sum of leftGroups length and the group index.
        return this.leftGroups.length + groupIndexForColumn;
      default:
        // Handle unexpected columnIndex values safely.
        console.warn(`Unsupported columnIndex: ${columnIndex}`);
        return null;  // Returning null to indicate an invalid case
    }
  }

  private setDefaultVariables() {
    this.wagonsData = null;
    this.trainWagonTitles = null;
    this.selectedWagonTitle = null;
    this.selectedUnitTitle = null;
    this.selectedTrain = TrainType.NONE;
    this.hasLeftColumnStatues = false;
    this.hasRightColumnErrors = false;
    this.progress = 0;
  }

  // This function that checks all the information under the sub tab and adds an icon based on whether there are any errors.
  public getSubTabWarningIcon(mainIndex: number, tabId: string): any {
    const isActiveTrainError: boolean = this.errorDetection.hasErrorsInTrain(tabId, this.activeTrainData);
    const isActiveWagonsError: boolean = this.errorDetection.hasErrorsInWagons(tabId, this.activeWagonsData);
    const isPassiveTrainError: boolean = this.isCoupling ? this.errorDetection.hasErrorsInTrain(tabId, this.passiveTrainData) : false;
    const isPassiveWagonsError: boolean = this.isCoupling ? this.errorDetection.hasErrorsInWagons(tabId, this.passiveWagonsData) : false;

    // If any tab (active or passive) has an error, mark the main tab with an error state
    if (isActiveTrainError || (this.numberOfWagons && isActiveWagonsError) || isPassiveTrainError || (this.secondNumberOfWagons && isPassiveWagonsError)) {
      this.mainTabsError[mainIndex] = State.Error;
      if (!this.isOldEnough) this.trainStatus = 'error';
      return {icon: 'warning-icon.png', pack: 'uruk', options: {color: '#FF1F00', class: 'nb-tab-custom-icon'}};
    }

    // If no errors, return null
    return null;
  }

  // Retrieves dynamic values for the specified group and info index based on the tab info.
  public getDynamicValues(groupIndex: number, infoIndex: number): { primaryValue: string; secondaryValue: string } {
    switch (this.currentView) {
      case TcddDialogView.TRAIN_OVERVIEW: {
        const primaryValue = this.getTrainDynamicValue(TrainType.ACTIVE, groupIndex, infoIndex);
        const secondaryValue = this.isCoupling ? this.getTrainDynamicValue(TrainType.PASSIVE, groupIndex, infoIndex) : null;
        return {primaryValue, secondaryValue};
      }
      case TcddDialogView.WAGON_DETAIL:
        return {
          primaryValue: this.getWagonDynamicValue(groupIndex, infoIndex),
          secondaryValue: null
        };
      case TcddDialogView.UNIT_DETAIL:
      case TcddDialogView.UNIT_3D_DETAIL:
        if (this.selectedUnitTitle === TCDD_UNIT.UDS_UIB) {
          return {
            primaryValue: this.getTrainDynamicValue(this.selectedTrain, groupIndex, infoIndex),
            secondaryValue: null
          };
        }

        return {
          primaryValue: this.getWagonDynamicValue(groupIndex, infoIndex),
          secondaryValue: null
        };
      case TcddDialogView.UNIT_VISUAL:
      case TcddDialogView.UNIT_3D:
        return {
          primaryValue: null,
          secondaryValue: null
        };
      default:
        return {primaryValue: null, secondaryValue: null};
    }
  }

  // Retrieves a dynamic value based on indexes for train
  private getTrainDynamicValue(trainType: string, groupIndex: number, infoIndex: number): string {
    try {
      const trainData = trainType === TrainType.PASSIVE
        ? this.passiveTrainData
        : this.activeTrainData;

      if (!trainData) {
        return null;
      }

      const value = trainData[this.activeSubTab]?.[groupIndex]?.[infoIndex];
      if (value === '' || value === null || value === undefined) return null;
      return String(value);
    } catch (error) {
      console.error('An error occurred while retrieving the dynamic value.');
      return null;
    }
  }

  // Retrieves a dynamic value based on indexes for train wagons
  private getWagonDynamicValue(groupIndex: number, infoIndex: number): string {
    try {
      if (!this.trainWagonTitles || !this.wagonsData) {
        return null;
      }

      // Find the index of the currently selected wagon title
      const wagonIndex: number = this.trainWagonTitles.indexOf(this.selectedWagonTitle);

      // Check if the selected wagon title exists in the train data
      if (wagonIndex === -1) {
        console.warn('Selected wagon title not found: ', this.selectedWagonTitle);
        return null;
      }

      const value = this.wagonsData[this.activeSubTab]?.[wagonIndex]?.[groupIndex]?.[infoIndex];
      if (value === '' || value === null || value === undefined) return null;
      return String(value);
    } catch (error) {
      console.error('An error occurred while retrieving the dynamic value.');
      return null;
    }
  }

  /**
   * Retrieves the text color based on the specified context and value.
   *
   * @param context - The context identifier for which the text color is required.
   * @param value - A value used to determine the appropriate text color.
   * @returns The resolved text color as a string.
   */
  public getTextColor(context: string, value: number): string {
    const rule = this.textColorRules.getRule(this.activeSubTab)?.[context];

    // Return the default color if no rule is defined.
    if (!rule || value == null) return Color.White;

    // If the rule is a function, compute and return the color using the provided value.
    if (typeof rule === 'function') return rule(value);

    // If the rule is an object, try to resolve the color based on the provided value.
    if (typeof rule === 'object') return rule[value] !== undefined ? rule[value] : rule.default || Color.White;

    // Fallback: return the default color for any unrecognized rule type.
    return Color.White;
  }

  /**
   * Determines the type of visual element to render based on the provided visual element string.
   *
   * @param visualElement - The visual element from the info object.
   * @returns 'badge' if the visual element contains 'badge'
   *          'svg' if the visual element contains 'svg',
   *          'text' if the visual element contains 'text',
   *          'icon' otherwise,
   *          'none' if the visual element is falsy.
   */
  public determineVisualElementType(visualElement: string): VisualElementType {
    if (!visualElement) return VisualElementType.NONE;

    const lowerVisualElement = visualElement.toLowerCase();
    if (lowerVisualElement.includes(VisualElementType.BADGE)) return VisualElementType.BADGE;
    if (lowerVisualElement.includes(VisualElementType.SVG)) return VisualElementType.SVG;
    if (lowerVisualElement.includes(VisualElementType.TEXT)) return VisualElementType.TEXT;

    return VisualElementType.ICON;
  }

  /**
   * Determines the appropriate icon classes based on the provided context and input value.
   *
   * @param iconContext - A string representing the icon context (e.g. 'error-0' or 'error-1').
   * @param iconTrigger - The value that, when matched with the input, triggers icon.
   * @param inputValue - The input value to evaluate.
   * @returns The determined icon classes.
   */
  public determineIcon(iconContext: string, iconTrigger: string, inputValue: unknown): string {
    // Normalize input
    const normalizedValue =
      typeof inputValue === 'string' ? inputValue.trim() : String(inputValue ?? '').trim();

    if (!normalizedValue || normalizedValue === TcddTrainDialogTemplateComponent.DEFAULT_PLACEHOLDER) return 'fa fa-question-circle';

    // Normalize the iconContext string for case-insensitive comparison.
    const context = iconContext?.toLowerCase() || '';

    for (const key of Object.keys(iconMap)) {
      if (context.includes(key)) {
        const config = iconMap[key];
        return normalizedValue === iconTrigger ? config.match : config.notMatch;
      }
    }

    // Default case: no matching context found.
    return 'fa fa-question-circle';
  }

  /**
   * Determines the badge status based on the provided badge context and input value.
   *
   * @param badgeContext - A string representing the badge context (e.g., 'active', 'isolated', or 'sos').
   * @param badgeTrigger - The value that, when matched with the normalized input, triggers the badge.
   * @param inputValue - The input value to evaluate.
   * @returns An object containing the determined badge status and, if applicable, an icon.
   */
  public determineBadge(badgeContext: string, badgeTrigger: string, inputValue: unknown): { badge: string, icon?: string } {
    // Normalize input
    const normalizedValue =
      typeof inputValue === 'string' ? inputValue.trim() : String(inputValue ?? '').trim();

    if (!normalizedValue || normalizedValue === TcddTrainDialogTemplateComponent.DEFAULT_PLACEHOLDER) return {badge: 'unknown'};

    const normalizedContext = badgeContext?.toLowerCase() || '';

    for (const key of Object.keys(badgeMap)) {
      if (normalizedContext.includes(key)) {
        const config = badgeMap[key];
        return {badge: normalizedValue === badgeTrigger ? config.match : config.notMatch};
      }
    }

    // Special situation: direction
    if (normalizedContext.includes('direction')) {
      if (normalizedValue === '1') { // forward
        return {badge: 'forward', icon: 'forward.svg'};
      } else if (normalizedValue === '2') { // backward
        return {badge: 'backward', icon: 'backward.svg'};
      }
      return {badge: 'not-direction'}; // none
    }

    // Special situation: hvac-mode
    if (normalizedContext.includes('hvac-mode')) {
      const badgeValue = hvacMode[normalizedValue] || 'unknown';
      if (normalizedValue === '1') {
        return {badge: badgeValue, icon: 'fire.svg'};
      }
      return {badge: badgeValue};
    }

    // Special situation: zero-speed-bypass-status
    if (normalizedContext.includes('zero-speed-bypass-status')) {
      if (normalizedValue === badgeTrigger) {
        return {badge: 'zero-speed-bypass-active', icon: 'small-warning.svg'};
      }
      return {badge: 'zero-speed-bypass-inactive'};
    }

    return {badge: 'unknown'};
  }

  public determineText(textContext: string, textTrigger: string, inputValue: unknown): string {
    const normalizedValue =
      typeof inputValue === 'string' ? inputValue.trim() : String(inputValue ?? '').trim();

    if (!normalizedValue || normalizedValue === TcddTrainDialogTemplateComponent.DEFAULT_PLACEHOLDER) return 'unknown';

    const normalizedContext = textContext?.toLowerCase() || '';

    // Special situation: vacuum
    if (normalizedContext.includes('vacuum')) {
      return vacuumMap[normalizedValue] || 'unknown';
    }

    // Special situation: pantograph
    if (normalizedContext.includes('pantograph')) {
      return pantographMap[normalizedValue] || 'unknown';
    }

    // Special situation: brake mode
    if (normalizedContext.includes('brake-mode')) {
      return brakeMode[normalizedValue] || 'unknown';
    }

    // Special situation: dmi status
    if (normalizedContext.includes('dmi-status')) {
      return dmiStatus[normalizedValue] || 'unknown';
    }

    for (const key of Object.keys(textMap)) {
      if (normalizedContext.includes(key)) {
        const config = textMap[key];
        return normalizedValue === textTrigger ? config.match : config.notMatch;
      }
    }

    return 'unknown';
  }

  /**
   * Determines the appropriate SVG asset based on the provided context and input value.
   *
   * @param svgContext - A string indicating the SVG context (e.g. 'svg-parkingBrake-1', etc.).
   * @param svgTrigger - The value used for matching with the normalized input.
   * @param inputValue - The input value to evaluate.
   * @returns The determined SVG asset filename as a string.
   */
  public determineSvg(svgContext: string, svgTrigger: string, inputValue: unknown): string {
    const normalizedValue =
      typeof inputValue === 'string' ? inputValue.trim() : String(inputValue ?? '').trim();

    if (!normalizedValue || normalizedValue === TcddTrainDialogTemplateComponent.DEFAULT_PLACEHOLDER) return 'default.svg';

    const config = svgMap[svgContext];
    if (!config) return 'default.svg';

    return normalizedValue === svgTrigger ? config.match : config.notMatch;
  }

  public onMainTabChange(event) {
    this.stackService.reset();
    this.setDefaultVariables();
    this.activeMainTab = event.tabId;

    if (this.activeMainTab === TcddMainSystem.DIAGNOSTIC_SYSTEM) {
      this.activeSubTab = TcddSubSystem.TRACTION_SYSTEM;

      this.updateGroupColumns();
      this.loadAndModifySVG(this.getSvgPath());
    }
  }

  /**
   * Sets the active tab based on the tab selection event.
   * @param event the tab selection event
   * */
  public onTabChange(event) {
    this.stackService.reset();
    this.setDefaultVariables();
    this.activeSubTab = event.tabId;

    this.updateGroupColumns();
    this.loadAndModifySVG(this.getSvgPath());
  }

  private getSvgPath(): string {
    return svgPaths[this.activeSubTab] || this.defaultSVGPath;
  }

  private initializeSVGManager() {
    this.svgManager = new SVGManagerUtil(this.sanitizer, this.translate, SvgTcddTrainRules.getRules(),
      SvgTcddTrainClickHandlers.getHandlers(), TcddTrainWagonsIndexManagerUtil.getIndexesForWagonSize(this.numberOfWagons),
      this.isActiveModificationEnabled, this.isActiveClickEnabled);
    if (this.isCoupling) {
      this.svgManagerPassive = new SVGManagerUtil(this.sanitizer, this.translate, SvgTcddTrainRules.getRules(),
        SvgTcddTrainClickHandlers.getHandlers(), TcddTrainWagonsIndexManagerUtil.getIndexesForWagonSize(this.secondNumberOfWagons),
        this.isPassiveModificationEnabled, this.isPassiveClickEnabled);
    }

    if (this.svgManager.resultEmitter) {
      this.svgManager.resultEmitter.subscribe((result) => {
        this.selectedTrain = TrainType.ACTIVE;
        this.handleSelectedResult(result);
      });
    }

    if (this.isCoupling && this.svgManagerPassive.resultEmitter) {
      this.svgManagerPassive.resultEmitter.subscribe((result) => {
        this.selectedTrain = TrainType.PASSIVE;
        this.handleSelectedResult(result);
      });
    }
  }

  private handleSelectedResult(selectedResult: any) {
    if (selectedResult) {
      this.trainWagonTitles = this.selectedTrain === TrainType.ACTIVE
        ? this.activeTrainWagonTitles
        : (this.selectedTrain === TrainType.PASSIVE ? this.passiveTrainWagonTitles : null);

      this.wagonsData = this.selectedTrain === TrainType.ACTIVE
        ? this.activeWagonsData
        : (this.selectedTrain === TrainType.PASSIVE ? this.passiveWagonsData : null);

      this.pathInfo = {
        path: selectedResult.path,
        path_3D: selectedResult.path_3D,
        pathTitle: selectedResult.pathTitle,
        view: selectedResult.view
      };
      this.currentView = selectedResult.view;

      this.selectedWagonTitle = this.isWagon() ? selectedResult.selectedTitle : this.selectedWagonTitle;
      this.selectedUnitTitle = this.isUnit() ? selectedResult.selectedTitle : null;

      this.stackService.push({pathInfo: this.pathInfo, selectedTitle: selectedResult.selectedTitle});
      this.updateGroupColumns();
      this.loadAndModifySVG(selectedResult.path ?? selectedResult.path_3D);
    }
  }

  // Is current view a wagon detail?
  private isWagon(): boolean {
    return this.currentView === TcddDialogView.WAGON_DETAIL;
  }

  // Is current view one of the unit views?
  private isUnit(): boolean {
    return [
      TcddDialogView.UNIT_DETAIL,
      TcddDialogView.UNIT_VISUAL,
      TcddDialogView.UNIT_3D_DETAIL,
      TcddDialogView.UNIT_3D
    ].includes(this.currentView);
  }

  /**
   * Updates the left and right columns based on the active tab type.
   * Dynamically selects the appropriate data source (mainInfoGroups or subInfoGroups) depending on the active tab.
   */
  private updateGroupColumns(): void {
    const selectedMainTab: MainTab = TcddTrainTab.getTabInfo()?.find(tab => tab.id === this.activeMainTab); // Retrieve the active main tab information.
    const selectedSubTab: SubTab = selectedMainTab?.subTabs?.find(tab => tab.id === this.activeSubTab); // Retrieve the active sub-tab from the main tab.
    const groupsData: InfoGroup[] | null = this.getGroupsData(selectedSubTab); // Get the corresponding groups data based on the current tab type.

    if (!groupsData) {
      this.leftGroups = [];
      this.rightGroups = [];
      return;
    }

    const {leftGroups, rightGroups} = this.processGroupData(groupsData);
    this.leftGroups = leftGroups;
    this.rightGroups = rightGroups;
  }

  /**
   * Helper function: Determines which data group to use from the selected subtab.
   * @param selectedSubTab - The selected subtab object.
   * @returns mainInfoGroups or subInfoGroups or null.
   */
  private getGroupsData(selectedSubTab: any): InfoGroup[] {
    switch (this.currentView) {
      case TcddDialogView.TRAIN_OVERVIEW:
        return selectedSubTab?.mainInfoGroups ?? [];
      case TcddDialogView.WAGON_DETAIL:
        return selectedSubTab?.subInfoGroups ?? [];
      case TcddDialogView.UNIT_DETAIL:
      case TcddDialogView.UNIT_3D_DETAIL:
        if (this.selectedUnitTitle === TCDD_UNIT.UDS_UIB) {
          return (selectedSubTab?.mainInfoGroups ?? []).map((group: InfoGroup) => {
            group.visibility = group.id.includes(TCDD_UNIT.UDS_UIB);
            return group;
          });
        }

        if (this.selectedUnitTitle === TCDD_UNIT.DMI) {
          return (selectedSubTab?.subInfoGroups ?? []).map((group: InfoGroup) => {
            group.visibility = group.id.includes(TCDD_UNIT.DMI);
            return group;
          });
        }

        return null;
      case TcddDialogView.UNIT_VISUAL:
      case TcddDialogView.UNIT_3D:
        return null;
      default:
        return null;
    }
  }

  /**
   * Processes the raw group data by separating it into 'status' and 'error' groups.
   * Depending on the presence of statuses and errors, groups are either split into two columns or organized as separate columns.
   *
   * @param groupsData - The raw group data to process.
   * @returns An object containing leftGroups and rightGroups arrays.
   */
  private processGroupData(groupsData: InfoGroup[]): { leftGroups: InfoGroup[]; rightGroups: InfoGroup[] } {
    if (!groupsData?.length) {
      return {leftGroups: [], rightGroups: []};
    }

    // Filter groups based on their id content.
    // Groups containing 'status' are assigned to statusGroups.
    // Groups containing 'error' are assigned to errorGroups.
    const statusGroups: InfoGroup[] = groupsData.filter((group: any) => group.id?.includes('status'));
    const errorGroups: InfoGroup[] = groupsData.filter((group: any) => group.id?.includes('error'));

    statusGroups.forEach((group: InfoGroup, groupIndex: number) => {
      if (group.visibility === false) return;

      group.visibility = group.infoList.some((infoItem: InfoItem, infoIndex: number) => {
        if (infoItem.visibility === false) return;

        const {primaryValue, secondaryValue} = this.getDynamicValues(groupIndex, infoIndex);
        if (primaryValue == null && secondaryValue == null) return infoItem.visibility;
        if ((primaryValue != null && primaryValue !== TcddTrainDialogTemplateComponent.DEFAULT_PLACEHOLDER) || (secondaryValue != null && secondaryValue !== TcddTrainDialogTemplateComponent.DEFAULT_PLACEHOLDER)) {
          return true;
        }
      });
    });
    this.hasLeftColumnStatues = statusGroups.some(group => group.visibility);

    errorGroups.forEach((group: InfoGroup, groupIndex: number) => {
      if (group.visibility === false) return;

      const currentGroupIndex = statusGroups.length + groupIndex;
      group.visibility = group.infoList.some((infoItem: InfoItem, infoIndex: number) => {
        if (infoItem.visibility === false) return;

        const {primaryValue, secondaryValue} = this.getDynamicValues(currentGroupIndex, infoIndex);
        if (primaryValue == null && secondaryValue == null) return infoItem.visibility;
        if ((primaryValue != null && primaryValue === infoItem.visualElementTrigger) || (secondaryValue != null && secondaryValue === infoItem.visualElementTrigger)) {
          return true;
        }
      });
    });
    this.hasRightColumnErrors = errorGroups.some(group => group.visibility);

    return this.hasRightColumnErrors
      ? this.hasLeftColumnStatues ? {leftGroups: statusGroups, rightGroups: errorGroups} : this.splitGroupsIntoTwoColumns(groupsData)
      : this.hasLeftColumnStatues ? this.splitGroupsIntoTwoColumns(statusGroups) : {leftGroups: [], rightGroups: []};
  }

  /**
   * Splits the status groups into two columns for a balanced layout.
   *
   * Each group's weight is determined solely by the length of its infoList (or 1 if not provided).
   * The groups are split so that the difference in total weight between the two columns is minimized.
   *
   * @param groups - The array of groups to split.
   * @returns An object containing leftGroups and rightGroups arrays.
   */
  private splitGroupsIntoTwoColumns(groups: InfoGroup[]): { leftGroups: InfoGroup[]; rightGroups: InfoGroup[] } {
    if (!groups || groups.length === 0) {
      return {leftGroups: [], rightGroups: []};
    }

    // Helper function: Calculate the weight of a group based on its own visibility and the visibility of its infoList items.
    const getGroupWeight = (group: InfoGroup): number => {
      // If the group itself is not visible, its weight is 0.
      if (group.visibility === false) {
        return 0;
      }
      // If no infoList is provided, assign a default weight of 1.
      if (!group.infoList || !Array.isArray(group.infoList)) {
        return 1;
      }
      // Filter the infoList to include only visible items (i.e., items with visibility true).
      const visibleItems = group.infoList.filter(item => item.visibility === true);
      // Return the count of visible items if any exist; otherwise, weight is 0.
      return visibleItems.length > 0 ? visibleItems.length : 0;
    };

    // Calculate the total weight of all groups, considering only those items/groups that are visible.
    const totalWeight = groups.reduce((sum, group) => sum + getGroupWeight(group), 0);

    let leftWeight = 0;
    let bestSplitIndex = 0;
    let bestDiff = Infinity;

    // Determine the optimal split index.
    for (let i = 0; i < groups.length; i++) {
      leftWeight += getGroupWeight(groups[i]);
      const rightWeight = totalWeight - leftWeight;
      const diff = Math.abs(leftWeight - rightWeight);

      if (diff <= bestDiff) {
        bestDiff = diff;
        bestSplitIndex = i + 1;
      }
    }

    return {
      leftGroups: groups.slice(0, bestSplitIndex),
      rightGroups: groups.slice(bestSplitIndex)
    };
  }

  public goBack(): void {
    // Exit if there is no SVG to go back to or if the train is already in overview
    if (!this.previousSVGPath || this.currentView === TcddDialogView.TRAIN_OVERVIEW) {
      return;
    }

    if (this.currentView === TcddDialogView.WAGON_DETAIL) {
      this.currentView = TcddDialogView.TRAIN_OVERVIEW;
      this.setDefaultVariables();
    } else {
      this.hasLeftColumnStatues = false;
      this.hasRightColumnErrors = false;
      this.progress = 0;
    }

    this.stackService.pop();
    this.updateGroupColumns();
    this.loadAndModifySVG(this.previousSVGPath);
  }

  public loadPrevSvg(): void {
    if (!this.trainWagonTitles?.length || !this.selectedWagonTitle || this.currentView !== TcddDialogView.WAGON_DETAIL) {
      console.warn('No train wagon titles or selected wagon title.');
      return;
    }

    // Special case: if SKA1, coupling is true, and train is 'passive', switch to active mode
    if (this.shouldSwitchToActive()) {
      this.switchToActiveMode();
    } else {
      // Otherwise, select the previous wagon title in the array
      if (!this.selectPrevWagonTitle()) {
        console.warn('Already at first wagon or selected title not found.');
        return;
      }
    }

    // Get the previous SVG path using the handler for activeSubTab
    const handler = SvgTcddTrainClickHandlers.getHandlers()[this.activeSubTab];
    const prevWagonPath = handler(this.selectedWagonTitle)?.path;

    this.stackService.updateCurrent(item => ({
      ...item,
      pathInfo: {
        ...item.pathInfo,
        path: prevWagonPath
      },
      selectedTitle: this.selectedWagonTitle
    }));

    if (!prevWagonPath) {
      console.error('No valid path found for the previous wagon.');
      return;
    }

    // Perform visual updates
    this.updateGroupColumns();
    this.loadAndModifySVG(prevWagonPath);
  }

  public loadNextSvg(): void {
    if (!this.trainWagonTitles?.length || !this.selectedWagonTitle || this.currentView !== TcddDialogView.WAGON_DETAIL) {
      console.warn('No train wagon titles or selected wagon title.');
      return;
    }

    // Special case: if SKA2, coupling is true, and train is 'active', switch to passive mode
    if (this.shouldSwitchToPassive()) {
      this.switchToPassiveMode();
    } else {
      // Otherwise, select the next wagon title in the array
      if (!this.selectNextWagonTitle()) {
        console.warn('Already at last wagon or selected title not found.');
        return;
      }
    }

    // Get the next SVG path using the handler for activeSubTab
    const handler = SvgTcddTrainClickHandlers.getHandlers()[this.activeSubTab];
    const nextWagonPath = handler(this.selectedWagonTitle)?.path;

    this.stackService.updateCurrent(item => ({
      ...item,
      pathInfo: {
        ...item.pathInfo,
        path: nextWagonPath
      },
      selectedTitle: this.selectedWagonTitle
    }));

    if (!nextWagonPath) {
      console.error('No valid path found for the next wagon.');
      return;
    }

    // Perform visual updates
    this.updateGroupColumns();
    this.loadAndModifySVG(nextWagonPath);
  }

  private shouldSwitchToPassive(): boolean {
    // If selected title is SKA2, coupling is true, and train is 'active', switch to passive
    return (
      this.selectedWagonTitle === TcddWagonTitle.SKA2 &&
      this.isCoupling &&
      this.selectedTrain === TrainType.ACTIVE
    );
  }

  private shouldSwitchToActive(): boolean {
    // If selected title is SKA1, coupling is true, and train is 'passive', switch to active
    return (
      this.selectedWagonTitle === TcddWagonTitle.SKA1 &&
      this.isCoupling &&
      this.selectedTrain === TrainType.PASSIVE
    );
  }

  private switchToPassiveMode(): void {
    // Load passive train data and set selected title to SKA1
    this.wagonsData = this.passiveWagonsData;
    this.trainWagonTitles = this.passiveTrainWagonTitles;
    this.selectedTrain = TrainType.PASSIVE;
    this.selectedWagonTitle = TcddWagonTitle.SKA1;
  }

  private switchToActiveMode(): void {
    // Load active train data and set selected title to SKA2
    this.wagonsData = this.activeWagonsData;
    this.trainWagonTitles = this.activeTrainWagonTitles;
    this.selectedTrain = TrainType.ACTIVE;
    this.selectedWagonTitle = TcddWagonTitle.SKA2;
  }

  private selectNextWagonTitle(): boolean {
    // Find index of current title
    const currentIndex: number = this.trainWagonTitles.indexOf(this.selectedWagonTitle);

    // If index invalid or at last element, return false
    if (currentIndex < 0 || currentIndex >= this.trainWagonTitles.length - 1) {
      return false;
    }

    // Select next title
    this.selectedWagonTitle = this.trainWagonTitles[currentIndex + 1];
    return true;
  }

  private selectPrevWagonTitle(): boolean {
    // Find index of current title
    const currentIndex: number = this.trainWagonTitles.indexOf(this.selectedWagonTitle);

    // If index invalid or at first element, return false
    if (currentIndex <= 0) {
      return false;
    }

    // Select prev title
    this.selectedWagonTitle = this.trainWagonTitles[currentIndex - 1];
    return true;
  }

  protected mapContextData(context: any): void {
    if (!context) {
      console.error('Context Data is missing');
      return;
    }
    // console.log('Context Data: ', context);
    this.isCoupling = !!context.passiveTrain;
    this.extractIndices();
    this.position = context.position;

    this.setupActiveTrain(context);
    if (this.isCoupling) {
      this.setupPassiveTrain(context);
    }

    this.mapCommonParameters(context);

    this.cdr.detectChanges();

    this.isDataLoaded = true;
    this.initializeSVGManager();
    this.updateGroupColumns();
    this.loadAndModifySVG(this.defaultSVGPath);
    this.getPM();
  }

  private extractIndices(): void {
    this.lightOnIndex = TcddTrainTabUtil.getValueIndex(TcddMainSystem.DIAGNOSTIC_SYSTEM, TcddSubSystem.OTHER_SYSTEM, TcddSubSystemType.MAIN, OtherSystemKeys.lightOn);
    this.halfLightIndex = TcddTrainTabUtil.getValueIndex(TcddMainSystem.DIAGNOSTIC_SYSTEM, TcddSubSystem.OTHER_SYSTEM, TcddSubSystemType.MAIN, OtherSystemKeys.halfLight);
    this.emergencyLightIndex = TcddTrainTabUtil.getValueIndex(TcddMainSystem.DIAGNOSTIC_SYSTEM, TcddSubSystem.OTHER_SYSTEM, TcddSubSystemType.MAIN, OtherSystemKeys.emergencyLight);
    this.tempOutsideIndex = TcddTrainTabUtil.getValueIndex(TcddMainSystem.DIAGNOSTIC_SYSTEM, TcddSubSystem.AIR_CONDITION_SYSTEM, TcddSubSystemType.MAIN, AirConditionSystemKeys.tmpOutside);
  }

  private setupActiveTrain(context: any): void {
    this.trainEntity = context.activeTrain;
    this.activeDeviceStatus = context.activeDeviceStatus;
    this.activeDeviceErrors = super.getValue(this.activeDeviceStatus, 'deviceErrors');
    this.activeTrainWagons = context.activeTrainWagons ?? [];

    this.trainNo = this.getSingleElementValue(this.position, 'activeHmiVehicleNo');
    this.numberOfWagons = super.getValue(this.trainEntity, 'numberOfWagons');
    this.activeTrainWagonTitles = this.sortWagonByTemplate( // Get and sort the wagon titles
      TcddTrainWagonsIndexManagerUtil.getWagonTitlesTemplate(),
      this.activeTrainWagons.map(w => super.getValue(w, 'wagonType')),
      this.numberOfWagons
    );

    // Data processing
    this.activeTrainData = this.dataManager.processTrainData(context.activeTrainStatus);
    this.activeWagonsData = this.dataManager.processWagonsData(this.activeTrainWagons, context.activeTrainWagonStatus);
    this.dataManager.setUIBData(super.getValue(this.trainEntity, 'softwareVersion'), this.activeDeviceStatus, this.activeDeviceErrors, this.activeTrainData);

    this.activeTrainData[TcddSubSystem.MAIN_SYSTEM][MainSystemKeys.couplingStatus] = this.isCoupling;

    // Variables(ConnectionIndicator) that track the direction of the trains or whether the coupler is open (0 = null, 1 = coupler, 2 = direction).
    // (e.g., [2, 0] means the train is moving to the left and there is nothing on its right).
    const directionStatus = this.dataManager.parseTrainDirectionStatus(super.getValue(context.activeTrainStatus, 'status'));
    this.activeWagonsData[TcddSubSystem.MAIN_SYSTEM][MainSystemKeys.connectionIndicator] = this.dataManager.setConnectionIndicator(directionStatus) ?? [0, 0];
    this.activeWagonsData[TcddSubSystem.MAIN_SYSTEM][MainSystemKeys.cabActive] = directionStatus?.sCabActive ?? '0';
    this.activeWagonsData[TcddSubSystem.MAIN_SYSTEM][MainSystemKeys.lightOn] = this.activeTrainData?.[TcddSubSystem.OTHER_SYSTEM]?.[this.lightOnIndex.groupIndex]?.[this.lightOnIndex.infoIndex] ?? '0';
    this.activeWagonsData[TcddSubSystem.MAIN_SYSTEM][MainSystemKeys.halfLight] = this.activeTrainData?.[TcddSubSystem.OTHER_SYSTEM]?.[this.halfLightIndex.groupIndex]?.[this.halfLightIndex.infoIndex] ?? '0';
    this.activeWagonsData[TcddSubSystem.MAIN_SYSTEM][MainSystemKeys.emergencyLight] = this.activeTrainData?.[TcddSubSystem.OTHER_SYSTEM]?.[this.emergencyLightIndex.groupIndex]?.[this.emergencyLightIndex.infoIndex] ?? '0';
    this.activeWagonsData[TcddSubSystem.MAIN_SYSTEM][MainSystemKeys.udsUibError] = (this.activeDeviceErrors ?? '').split('').some(bit => bit === '1');
    this.activeWagonsData[TcddSubSystem.MAIN_SYSTEM][MainSystemKeys.udsUibWagonIndex] = this.getWagonTitleIndex(this.activeTrainWagonTitles, TcddWagonTitle.OA3);
    this.activeWagonsData[TcddSubSystem.MAIN_SYSTEM][MainSystemKeys.leadFollowConsist] = this.isCoupling ? '2' : '0';
    this.activeWagonsData[TcddSubSystem.MAIN_SYSTEM][MainSystemKeys.wagonIndex] = TcddTrainWagonsIndexManagerUtil.getIndexesForWagonSize(this.numberOfWagons);

    this.activeSignalStatus = ConnectionUtil.getConnectionStatus(
      super.getValue(this.activeDeviceStatus, 'gsmConnection'),
      super.getValue(this.activeDeviceStatus, 'gsmSignalQuality'),
      super.getValue(this.activeDeviceStatus, 'lifeSign', 'observedAt'),
      signalQualityMap,
      thresholds);
  }

  private setupPassiveTrain(context: any): void {
    this.secondTrainEntity = context.passiveTrain;
    this.passiveDeviceStatus = context.passiveDeviceStatus;
    this.passiveDeviceErrors = super.getValue(this.passiveDeviceStatus, 'deviceErrors');
    this.passiveTrainWagons = context.passiveTrainWagons ?? [];

    this.secondTrainNo = this.getSingleElementValue(this.position, 'passiveHmiVehicleNo');
    this.secondNumberOfWagons = super.getValue(this.secondTrainEntity, 'numberOfWagons');
    this.passiveTrainWagonTitles = this.sortWagonByTemplate( // Get and sort the wagon titles
      TcddTrainWagonsIndexManagerUtil.getWagonTitlesTemplate(),
      this.passiveTrainWagons.map(w => super.getValue(w, 'wagonType')),
      this.secondNumberOfWagons
    );

    // Data processing
    this.passiveTrainData = this.dataManager.processTrainData(context.passiveTrainStatus);
    this.passiveWagonsData = this.dataManager.processWagonsData(this.passiveTrainWagons, context.passiveTrainWagonStatus);
    this.dataManager.setUIBData(super.getValue(this.secondTrainEntity, 'softwareVersion'), this.passiveDeviceStatus, this.passiveDeviceErrors, this.passiveTrainData);

    this.passiveTrainData[TcddSubSystem.MAIN_SYSTEM][MainSystemKeys.couplingStatus] = this.isCoupling;

    // Variables(ConnectionIndicator) that track the direction of the trains or whether the coupler is open (0 = null, 1 = coupler, 2 = direction).
    // (e.g., [2, 0] means the train is moving to the left and there is nothing on its right).
    const directionStatus = this.dataManager.parseTrainDirectionStatus(super.getValue(context.passiveTrainStatus, 'status'));
    this.passiveWagonsData[TcddSubSystem.MAIN_SYSTEM][MainSystemKeys.connectionIndicator] = this.dataManager.setConnectionIndicator(directionStatus) ?? [0, 0];
    this.passiveWagonsData[TcddSubSystem.MAIN_SYSTEM][MainSystemKeys.cabActive] = directionStatus?.sCabActive ?? '0';
    this.passiveWagonsData[TcddSubSystem.MAIN_SYSTEM][MainSystemKeys.lightOn] = this.passiveTrainData?.[TcddSubSystem.OTHER_SYSTEM]?.[this.lightOnIndex.groupIndex]?.[this.lightOnIndex.infoIndex] ?? '0';
    this.passiveWagonsData[TcddSubSystem.MAIN_SYSTEM][MainSystemKeys.halfLight] = this.passiveTrainData?.[TcddSubSystem.OTHER_SYSTEM]?.[this.halfLightIndex.groupIndex]?.[this.halfLightIndex.infoIndex] ?? '0';
    this.passiveWagonsData[TcddSubSystem.MAIN_SYSTEM][MainSystemKeys.emergencyLight] = this.passiveTrainData?.[TcddSubSystem.OTHER_SYSTEM]?.[this.emergencyLightIndex.groupIndex]?.[this.emergencyLightIndex.infoIndex] ?? '0';
    this.passiveWagonsData[TcddSubSystem.MAIN_SYSTEM][MainSystemKeys.udsUibError] = (this.passiveDeviceErrors ?? '').split('').some(bit => bit === '1');
    this.passiveWagonsData[TcddSubSystem.MAIN_SYSTEM][MainSystemKeys.udsUibWagonIndex] = this.getWagonTitleIndex(this.passiveTrainWagonTitles, TcddWagonTitle.OA3);
    this.passiveWagonsData[TcddSubSystem.MAIN_SYSTEM][MainSystemKeys.leadFollowConsist] = '1';
    this.passiveWagonsData[TcddSubSystem.MAIN_SYSTEM][MainSystemKeys.wagonIndex] = TcddTrainWagonsIndexManagerUtil.getIndexesForWagonSize(this.secondNumberOfWagons);

    this.passiveSignalStatus = ConnectionUtil.getConnectionStatus(
      super.getValue(this.passiveDeviceStatus, 'gsmConnection'),
      super.getValue(this.passiveDeviceStatus, 'gsmSignalQuality'),
      super.getValue(this.passiveDeviceStatus, 'lifeSign', 'observedAt'),
      signalQualityMap,
      thresholds);
  }

  private mapCommonParameters(context: any): void {
    const coords: number[] = this.getSingleElementValue(this.position, 'location').coordinates;
    this.location = coords.map(c => c.toFixed(TcddTrainDialogTemplateComponent.COORD_DECIMALS)).join(', ');
    this.speed = this.getSingleElementValue(this.position, 'speed');

    this.outsideAirTemperature = this.activeTrainData?.[TcddSubSystem.AIR_CONDITION_SYSTEM]?.[this.tempOutsideIndex.groupIndex]?.[this.tempOutsideIndex.infoIndex] ?? '-';

    this.isOldEnough = this.isObservedAtOldEnough(super.getValue(this.activeDeviceStatus, 'lifeSign', 'observedAt'));
    if (this.isOldEnough) this.trainStatus = 'not-connection';
  }

  private sortWagonByTemplate(template: string[], wagon: string[], numberOfWagons: number): string[] {
    if (numberOfWagons == null) return [];
    if (!wagon || wagon.length === 0) {
      return Object
        .entries(TcddTrainWagonsIndexManagerUtil.getIndexesForWagonSize(numberOfWagons))
        .sort(([, idxA], [, idxB]) => idxA - idxB)
        .map(([title]) => title as string);
    }

    const templateLowerCase = template.map(item => item.toLowerCase());
    const wagonLowerCase = wagon.map(item => item.toLowerCase());

    return templateLowerCase.filter(item => wagonLowerCase.includes(item));
  }

  private getWagonTitleIndex(titles: string[], title: string): number | null {
    const idx = titles.indexOf(title);
    return idx >= 0 ? idx : null;
  }

  /**
   * Checks whether the given `observedAt` timestamp is at least
   * `minAgeMinutes` minutes in the past.
   *
   * @param observedAt  An ISO‑format date/time string
   * @param minAgeMinutes  Minimum age in minutes (default: 10)
   * @throws Error if `observedAt` is not a valid date string
   */
  private isObservedAtOldEnough(observedAt: string, minAgeMinutes: number = 10): boolean {
    const observedDate: Date = new Date(observedAt);
    if (isNaN(observedDate.getTime())) {
      throw new Error(`Invalid date format: "${observedAt}"`);
    }

    const currentDate: Date = new Date();
    const differenceInMs: number = currentDate.getTime() - observedDate.getTime();
    const differenceInMinutes: number = differenceInMs / (1000 * 60);
    return differenceInMinutes >= minAgeMinutes;
  }

  private loadAndModifySVG(svgPath: string) {
    this.backIcon = document.getElementById(`backContainer_${this.activeSubTab}`);
    this.prevIcon = document.getElementById(`prevIcon_${this.activeSubTab}`);
    this.nextIcon = document.getElementById(`nextIcon_${this.activeSubTab}`);

    // Status check for svg path updates
    this.updateSVGPaths(svgPath);

    // Update visibility of navigation icons
    this.updateBackIconVisibility(this.backIcon);
    this.updateNavigationIconsVisibility(this.prevIcon, this.nextIcon);

    // Update visibility of containers based on selectedTrain
    if (this.containerActive) this.containerActive.innerHTML = '';
    if (this.containerPassive) this.containerPassive.innerHTML = '';

    this.containerActive = document.getElementById(`svgContainer_${this.activeSubTab}`);
    this.containerPassive = document.getElementById(`svgContainerPassive_${this.activeSubTab}`);
    this.modelViewerContainer = document.getElementById(`modelViewerContainer_${this.activeSubTab}`);

    this.updateSvgContainersVisibility(this.containerActive, this.containerPassive);
    this.updateModelViewerContainerVisibility(this.modelViewerContainer);

    if (this.currentView === TcddDialogView.UNIT_3D || this.currentView === TcddDialogView.UNIT_3D_DETAIL) return;

    // Handle active train
    this.handleActiveTrain(svgPath, this.containerActive);

    // Handle passive train
    this.handlePassiveTrain(svgPath, this.containerPassive);
  }

  // Updates SVG path information and adjusts it according to the state information.
  private updateSVGPaths(svgPath: string): void {
    switch (this.currentView) {
      case TcddDialogView.TRAIN_OVERVIEW: {
        this.previousSVGPath = null;
        this.currentSVGPath = svgPath;
        this.activeSubTabSVGPath = svgPath;
        break;
      }
      case TcddDialogView.WAGON_DETAIL: {
        this.previousSVGPath = this.activeSubTabSVGPath;
        this.currentSVGPath = svgPath;
        break;
      }
      default: {
        this.previousSVGPath = this.currentSVGPath;
        this.currentSVGPath = svgPath;
        break;
      }
    }
  }

  /**
   * Executes operations related to the active train.
   */
  private handleActiveTrain(svgPath: string, containerActive: HTMLElement | null): void {
    if (Number.isInteger(this.numberOfWagons) && [3, 4, 5, 6, 8].includes(this.numberOfWagons)
      && (!this.selectedTrain || this.selectedTrain === TrainType.ACTIVE)) {
      if (this.svgManager && containerActive) {
        // console.log('Active Train Data: ', this.activeTrainData);
        // console.log('Active Wagons Data: ', this.activeWagonsData);
        const activeSvgPath: string = this.currentView === TcddDialogView.TRAIN_OVERVIEW ? `${svgPath}-${this.numberOfWagons}-wagons.svg` : svgPath;
        this.svgManager.loadAndModifySVG(containerActive, activeSvgPath, this.activeWagonsData, this.activeSubTab);
      }
    }
  }

  /**
   * Executes operations related to the passive train.
   */
  private handlePassiveTrain(svgPath: string, containerPassive: HTMLElement | null): void {
    if (Number.isInteger(this.secondNumberOfWagons) && [3, 4, 5, 6, 8].includes(this.secondNumberOfWagons)
      && (!this.selectedTrain || this.selectedTrain === TrainType.PASSIVE)) {
      if (this.svgManagerPassive && containerPassive) {
        // console.log('Passive Train Data: ', this.passiveTrainData);
        // console.log('Passive Wagons Data: ', this.passiveWagonsData);
        const passiveSvgPath: string = this.currentView === TcddDialogView.TRAIN_OVERVIEW ? `${svgPath}-${this.secondNumberOfWagons}-wagons.svg` : svgPath;
        this.svgManagerPassive.loadAndModifySVG(containerPassive, passiveSvgPath, this.passiveWagonsData, this.activeSubTab);
      }
    }
  }

  /**
   * Updates the visibility of the active and passive SVG containers based on `selectedTrain`.
   * @param activeContainer - The SVG container for the active train.
   * @param passiveContainer - The SVG container for the passive train.
   */
  private updateSvgContainersVisibility(activeContainer: HTMLElement | null, passiveContainer: HTMLElement | null): void {
    const isRelevantTab = [TcddDialogView.TRAIN_OVERVIEW, TcddDialogView.WAGON_DETAIL, TcddDialogView.UNIT_DETAIL, TcddDialogView.UNIT_VISUAL].includes(this.currentView);
    const isActiveOrAll = this.selectedTrain === '' || this.selectedTrain === TrainType.ACTIVE;
    const isPassiveOrAll = this.selectedTrain === '' || this.selectedTrain === TrainType.PASSIVE;

    if (activeContainer) {
      activeContainer.style.display = (isRelevantTab && isActiveOrAll) ? 'flex' : 'none';
    }

    if (passiveContainer) {
      passiveContainer.style.display = (isRelevantTab && isPassiveOrAll) ? 'flex' : 'none';
    }
  }

  private updateModelViewerContainerVisibility(modelViewerContainer: HTMLElement | null): void {
    if (modelViewerContainer) {
      const is3DView =
        this.currentView === TcddDialogView.UNIT_3D ||
        this.currentView === TcddDialogView.UNIT_3D_DETAIL;

      modelViewerContainer.style.display = is3DView ? 'flex' : 'none';
    }
  }

  private updateBackIconVisibility(backIcon: HTMLElement | null): void {
    if (backIcon) {
      backIcon.style.display = this.previousSVGPath ? 'block' : 'none';
    }
  }

  private updateNavigationIconsVisibility(prevIcon: HTMLElement | null, nextIcon: HTMLElement | null): void {
    if (prevIcon && nextIcon) {
      const visibility = this.getNavigationIconVisibility();
      prevIcon.style.display = visibility.prev;
      nextIcon.style.display = visibility.next;
    }
  }

  private getNavigationIconVisibility(): { prev: string, next: string } {
    const iconVisibility = {
      showBoth: {prev: 'block', next: 'block'},
      showPrevOnly: {prev: 'block', next: 'none'},
      showNextOnly: {prev: 'none', next: 'block'},
      hideBoth: {prev: 'none', next: 'none'}
    };

    if (this.currentView !== TcddDialogView.WAGON_DETAIL) return iconVisibility.hideBoth;

    if ((this.selectedWagonTitle === TcddWagonTitle.SKA1 && this.isCoupling && this.selectedTrain === TrainType.PASSIVE) ||
      (this.selectedWagonTitle === TcddWagonTitle.SKA2 && this.isCoupling && this.selectedTrain === TrainType.ACTIVE)
    ) {
      return iconVisibility.showBoth;
    }

    // Return the visibility based on selectedTitle
    switch (this.selectedWagonTitle) {
      case TcddWagonTitle.SKA1:
        return iconVisibility.showNextOnly;
      case TcddWagonTitle.SKA2:
        return iconVisibility.showPrevOnly;
      case TcddWagonTitle.OA1:
      case TcddWagonTitle.OA1_1:
      case TcddWagonTitle.OA1_2:
      case TcddWagonTitle.OA2:
      case TcddWagonTitle.OA3:
      case TcddWagonTitle.OA4:
        return iconVisibility.showBoth;
      default:
        return iconVisibility.hideBoth;
    }
  }

  public getWagonTitle(): string {
    const consistData: string = this.wagonsData?.mainSystem?.['leadFollowConsist'];
    const wagon: string = this.selectedWagonTitle;

    if (consistData === '2') {
      return 'primary train' + ' - ' + wagon;
    }

    if (consistData === '1') {
      return 'secondary train' + ' - ' + wagon;
    }

    return wagon;
  }

  private getSingleElementValue(entity, field, valueField = 'value'): any {
    const elementValue = super.getValue(entity, field, valueField);
    if (Array.isArray(elementValue)) {
      return elementValue[0][valueField];
    } else {
      return elementValue;
    }
  }
}
